import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import { Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { useMutation } from "@tanstack/react-query";
import VisaLogo from "../../../../../assets/images/icons/visa-logo.svg?react";
import MastercardLogo from "../../../../../assets/images/icons/mastercard-logo.svg?react";
import BacsLogo from "../../../../../assets/images/icons/bacs-logo.svg?react";
import { stripePromise } from "../../../../../services/stripeConfig";
import "./DefaultPaymentForm.css";
import Preloader from "../../../../../components/Preloader";
import axiosManager from "../../../../../services/axiosManager";
import { useStudents } from "../../../../../context/students";
import { useToast } from "../../../../../context/toast";
import BaseDialog from "../../../../../components/BaseDialog";

const DefaultPaymentForm = (props) => {
    const {
        open, onClose,
        isLoading, defaultPayment, refetch,
        paymentMethods,
    } = props;
    const { t } = useTranslation();
    const toast = useToast();
    const { activeStudentId } = useStudents();

    const [isEditMode, setIsEditMode] = useState(false);

    const { mutateAsync: setupNewMethod, isPending: isSettingNewMethod } = useMutation({
        mutationFn: () => axiosManager.post$(`/parent/api/students/${activeStudentId}/paymentmethods`).then((response) => response?.data),
        onError: () => toast.error(t("common.errorMessage")),
    });
    const { mutateAsync: setNewMethodId, isPending: isSettingNewMethodId } = useMutation({
        mutationFn: (paymentId) => axiosManager.post$(`/parent/api/students/${activeStudentId}/paymentmethods/${paymentId}`).then((response) => response?.data),
        onError: () => toast.error(t("common.errorMessage")),
    });
    const { mutateAsync: getNewMethods, isPending: isLoadingNewMethods } = useMutation({
        mutationFn: () => axiosManager.get$(`/parent/api/students/${activeStudentId}/paymentmethods`).then((response) => response?.data),
        onError: () => toast.error(t("common.errorMessage")),
    });

    const fetchClientSecret = useCallback(() => setupNewMethod()
        .then((data) => data.clientSecret), [setupNewMethod]);

    const handleClose = async () => {
        try {
            const newMethods = await getNewMethods();
            const oldMethodsIds = paymentMethods.map((it) => it.id);
            const newMethodId = newMethods.find((item) => !oldMethodsIds.includes(item.id))?.id;

            if (newMethodId) await setNewMethodId(newMethodId);
            await refetch();
        } catch {
            // silently
        }
        setIsEditMode(false);
        onClose();
    };
    const handleSetMethod = async () => {
        await setupNewMethod();
        setIsEditMode(true);
    };

    return (
        <BaseDialog
            withCloseButton
            open={open}
            onClose={handleClose}
            isBig
        >
            {isEditMode ? (
                <div className="DefaultPaymentForm">
                    <Typography variant="h4" component="div" className="text-primary" gutterBottom>
                        {t("payments.paymentInformation")}
                    </Typography>
                    <Typography variant="body1" component="div" gutterBottom>
                        {t("payments.paymentInformationSubtitle")}
                    </Typography>
                    <div className="mt-4" />
                    <EmbeddedCheckoutProvider
                        stripe={stripePromise}
                        options={{ fetchClientSecret }}
                    >
                        <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                </div>
            ) : (
                <div className="DefaultPaymentForm">
                    <Typography variant="h4" component="div" className="text-primary" gutterBottom>
                        {t("payments.yourDefaultPaymentMethod")}
                    </Typography>
                    <Typography variant="body1" component="div">
                        {t("payments.yourDefaultPaymentMethodSubtitle")}
                    </Typography>
                    <Preloader isLoading={isLoading} size={30}>
                        <div className="DefaultPaymentForm__card">
                            {defaultPayment ? (
                                <>
                                    <Typography
                                        variant="h5"
                                        component="span"
                                        className="DefaultPaymentForm__card-title text-primary"
                                    >
                                        {t("payments.card")}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        component="span"
                                        className="DefaultPaymentForm__card-number"
                                    >
                                        {`XXXX - ${defaultPayment.card?.last4Digits || defaultPayment.bacs?.last4Digits}`}
                                    </Typography>
                                    {defaultPayment.card ? (
                                        <span className="DefaultPaymentForm__card-icon">
                                            {defaultPayment.card?.brand === "visa" ? <VisaLogo /> : <MastercardLogo />}
                                        </span>
                                    ) : (
                                        <span className="DefaultPaymentForm__card-icon">
                                            <BacsLogo />
                                        </span>
                                    )}
                                </>
                            ) : (
                                <Typography
                                    variant="h5"
                                    component="span"
                                    className="DefaultPaymentForm__card-title text-primary"
                                >
                                    {t("payments.yourDefaultNoSavedMethod")}
                                </Typography>
                            )}
                        </div>
                    </Preloader>
                    <div className="w-full flex align-center gap-4 mt-8">
                        <Button
                            variant="outlined"
                            className="w-full"
                            onClick={handleClose}
                            disabled={isSettingNewMethod || isLoadingNewMethods || isSettingNewMethodId}
                        >
                            {t("button.close")}
                        </Button>
                        <Button
                            variant="contained"
                            className="w-full button-accent"
                            onClick={handleSetMethod}
                            loading={isSettingNewMethod || isLoadingNewMethods || isSettingNewMethodId}
                        >
                            {defaultPayment ? t("payments.updateMethod") : t("payments.addMethod")}
                        </Button>
                    </div>
                </div>
            )}
        </BaseDialog>
    );
};

export default DefaultPaymentForm;
